import { MATCH_STATUS, isCompleted } from '@/constants/matchStatus';
import { getCompetitionRule } from '@/data/competitionRules';
import type { BracketSideTemplate, KnockoutStage } from '@/data/competitionRules/brackets/types';
import { placeholderId } from '@/data/injectKnockoutBracket';

/**
 * Play-off bracket model. With a template: the template's rounds, each filled
 * with the ties stored for it or, while none is stored, with the template's
 * placeholders. Without one: the stored knockout stages as they are and, for a
 * cup, placeholder rounds from the last stored one to the final.
 * Pure - nothing here is fetched, cached or persisted.
 */

/** The match fields the model reads; the league matches list satisfies it. */
export interface BracketMatch {
  id: number;
  team1: string;
  team2: string;
  team1_logo_url?: string | null;
  team2_logo_url?: string | null;
  match_date: string;
  status: string;
  score_team1?: number | null;
  score_team2?: number | null;
  stage?: string | null;
  matchday?: number | null;
  is_qualification?: boolean;
  /** Side the source marks as the winner of this match, by this match's own team1 / team2. */
  winner?: 'team1' | 'team2' | null;
}

export interface BracketSide {
  /** Team name; `null` while the side is a placeholder. */
  team: string | null;
  logoUrl: string | null;
  /** Who takes the side; set exactly when `team` is `null`. */
  placeholder: BracketSideTemplate | null;
  /** Goals over the legs that have a score; `null` before the first of them. */
  aggregate: number | null;
}

export interface BracketTie<M extends BracketMatch> {
  /** Unique within the app: the id of the first leg, or a negative placeholder id. */
  id: number;
  sides: [BracketSide, BracketSide];
  /** Stored matches by kick-off; empty for a placeholder. */
  legs: M[];
  /**
   * Index of the side that goes through: the aggregate decides; a level one is
   * decided by the winner mark of the last leg. `null` while undecided, or level
   * without that mark.
   */
  winner: 0 | 1 | null;
}

export interface BracketRound<M extends BracketMatch> {
  /**
   * Name of the round: the template's or, without a template, the stored stage
   * name. `null` for the template round found by position.
   */
  stage: string | null;
  /** Stage name stored on the round's matches; `null` while the ties are placeholders. */
  storedStage: string | null;
  /** Matches per tie. */
  legs: 1 | 2;
  /** True when the ties are stored ones, false when they are placeholders. */
  isStored: boolean;
  ties: BracketTie<M>[];
  /**
   * Whether tie `n` of the next round is fed by the ties at its position in
   * this one, so connector lines may be drawn. Always false on the last round.
   */
  connectsToNext: boolean;
}

const kickoff = (match: BracketMatch): number => Date.parse(match.match_date);

function storedTie<M extends BracketMatch>(legs: M[], legsPerTie: number): BracketTie<M> {
  legs.sort((a, b) => kickoff(a) - kickoff(b) || a.id - b.id);
  const first = legs[0];
  const side = (team: string, logoUrl: string | null | undefined): BracketSide => {
    const goals = legs.flatMap(leg =>
      leg.score_team1 == null || leg.score_team2 == null ? [] : [leg.team1 === team ? leg.score_team1 : leg.score_team2],
    );
    return {
      team,
      logoUrl: logoUrl ?? null,
      placeholder: null,
      aggregate: goals.length > 0 ? goals.reduce((sum, n) => sum + n, 0) : null,
    };
  };
  const sides: [BracketSide, BracketSide] = [
    side(first.team1, first.team1_logo_url),
    side(first.team2, first.team2_logo_url),
  ];
  const [a, b] = [sides[0].aggregate, sides[1].aggregate];
  const complete = legs.length >= legsPerTie && legs.every(leg => isCompleted(leg.status)) && a != null && b != null;
  // Only the deciding leg's mark settles a level tie; that leg may have the home side swapped.
  const last = legs[legs.length - 1];
  const markedTeam = last.winner ? last[last.winner] : null;
  let winner: 0 | 1 | null = null;
  if (complete && a !== b) winner = a > b ? 0 : 1;
  else if (complete && markedTeam) winner = markedTeam === first.team1 ? 0 : 1;
  return { id: first.id, sides, legs, winner };
}

/** The matches of one round grouped into ties: by stage and unordered team pair. */
function groupLegs<M extends BracketMatch>(matches: M[]): M[][] {
  const legsByPair = new Map<string, M[]>();
  for (const match of matches) {
    const key = [match.stage, ...[match.team1, match.team2].sort()].join('\n');
    legsByPair.set(key, [...(legsByPair.get(key) ?? []), match]);
  }
  return [...legsByPair.values()];
}

/**
 * Stored ties in display order: by the pair of table places (1/2, 3/4, ...) of
 * the better placed team, then by kick-off; the ties whose team plays in the
 * next round come first, in the order of the ties they feed there.
 */
function orderTies<M extends BracketMatch>(
  ties: BracketTie<M>[],
  nextTies: BracketTie<M>[],
  rankByTeam: ReadonlyMap<string, number>,
): BracketTie<M>[] {
  // A team with no table place sorts last: Infinity - Infinity is NaN, which falls through to kick-off.
  const seedPair = (tie: BracketTie<M>): number =>
    Math.floor((Math.min(...tie.sides.map(s => rankByTeam.get(s.team ?? '') ?? Infinity)) - 1) / 2);
  const bySeed = [...ties].sort((a, b) => seedPair(a) - seedPair(b) || kickoff(a.legs[0]) - kickoff(b.legs[0]));
  const feeders = nextTies.flatMap(next =>
    next.sides.flatMap(side => bySeed.filter(tie => tie.sides.some(s => s.team === side.team))),
  );
  return [...new Set([...feeders, ...bySeed])];
}

function connects<M extends BracketMatch>(round: BracketRound<M>, next: BracketRound<M>): boolean {
  const perTie = round.ties.length / next.ties.length;
  if ((perTie !== 1 && perTie !== 2) || round.isStored !== next.isStored) return false;
  if (!round.isStored) return true;
  return round.ties.every((tie, i) =>
    tie.sides.some(s => next.ties[Math.floor(i / perTie)].sides.some(n => n.team === s.team)),
  );
}

/**
 * Rounds of a competition without a template: its stored stages that carry no
 * matchday number, by first kick-off, ties by kick-off. Which tie feeds which
 * is not known, so no round connects to the next.
 */
function storedRounds<M extends BracketMatch>(matches: M[]): BracketRound<M>[] {
  const byStage = new Map<string, M[]>();
  for (const match of matches) {
    if (match.stage && match.matchday == null) byStage.set(match.stage, [...(byStage.get(match.stage) ?? []), match]);
  }
  return [...byStage]
    .map(([stage, stageMatches]): BracketRound<M> => {
      const pairs = groupLegs(stageMatches);
      // Two-legged when most ties hold two matches: one replayed tie does not turn the round.
      const legs = pairs.filter(pair => pair.length > 1).length * 2 > pairs.length ? 2 : 1;
      const ties = pairs
        .map(pair => storedTie(pair, legs))
        .sort((a, b) => kickoff(a.legs[0]) - kickoff(b.legs[0]) || a.id - b.id);
      return { stage, storedStage: stage, legs, isStored: true, ties, connectsToNext: false };
    })
    .sort((a, b) => kickoff(a.ties[0].legs[0]) - kickoff(b.ties[0].legs[0]));
}

/** Rounds that close a cup, from the final back: the round at index `n` holds `2 ** n` ties. */
const CLOSING_STAGES: KnockoutStage[] = ['Final', 'Semi-finals', 'Quarter-finals', 'Round of 16', 'Round of 32'];

/**
 * Placeholder rounds from the last stored round of a cup to its final, each
 * with half the ties of the round before. Pairs are set by a draw, so a side
 * names the previous round only and no round connects. Empty unless the last
 * round holds a power of two of ties that a closing stage follows.
 */
function closingRounds<M extends BracketMatch>(
  stored: BracketRound<M>[],
  leagueCode: string,
  season: string,
): BracketRound<M>[] {
  const size = Math.log2(stored[stored.length - 1]?.ties.length ?? 1);
  if (!Number.isInteger(size) || size > CLOSING_STAGES.length) return [];
  return Array.from({ length: size }, (_, i): BracketRound<M> => {
    const n = size - 1 - i;
    const side: BracketSide = {
      team: null,
      logoUrl: null,
      placeholder: { kind: 'winnerOfRound', round: stored.length + i - 1 },
      aggregate: null,
    };
    return {
      stage: CLOSING_STAGES[n],
      storedStage: null,
      legs: 1,
      isStored: false,
      ties: Array.from({ length: 2 ** n }, (_, slot) => ({
        id: placeholderId({ leagueCode, season, round: n, slot, leg: 0 }),
        sides: [side, side],
        legs: [],
        winner: null,
      })),
      connectsToNext: false,
    };
  });
}

function buildRounds<M extends BracketMatch>(
  matches: readonly M[],
  leagueCode: string,
  season: string,
  rankByTeam: ReadonlyMap<string, number>,
): BracketRound<M>[] {
  const main = matches.filter(m => m.is_qualification !== true && m.status !== MATCH_STATUS.CANCELLED);
  const template = getCompetitionRule(leagueCode, season)?.tieBracket;
  if (!template) {
    const stored = storedRounds(main);
    // A cup has no numbered round; play-off ties that end a league season are not continued.
    const isCup = main.every(m => m.matchday == null);
    return isCup ? [...stored, ...closingRounds(stored, leagueCode, season)] : stored;
  }

  const namedStages = new Set<string | null | undefined>(template.map(r => r.stage));

  const rounds: BracketRound<M>[] = [];
  for (let r = template.length - 1; r >= 0; r--) {
    const { stage, legs, slots } = template[r];
    // A round without a name takes the unnumbered rounds no named round claims.
    const stored = groupLegs(
      main.filter(m => (stage ? m.stage === stage : m.stage && m.matchday == null && !namedStages.has(m.stage))),
    ).map(pair => storedTie(pair, legs));
    const isStored = stored.length > 0;
    const ties = isStored
      ? orderTies(stored, rounds[0]?.ties ?? [], rankByTeam)
      : slots.map(
          ([a, b], slot): BracketTie<M> => ({
            id: placeholderId({ leagueCode, season, round: r, slot, leg: 0 }),
            sides: [
              { team: null, logoUrl: null, placeholder: a, aggregate: null },
              { team: null, logoUrl: null, placeholder: b, aggregate: null },
            ],
            legs: [],
            winner: null,
          }),
        );
    const storedStage = stored[0]?.legs[0].stage ?? null;
    rounds.unshift({ stage, storedStage, legs, isStored, ties, connectsToNext: false });
  }
  rounds.forEach((round, r) => {
    if (r < rounds.length - 1) round.connectsToNext = connects(round, rounds[r + 1]);
  });
  return rounds;
}

/**
 * The play-off bracket of one league-season; empty when its competition has no
 * template and no knockout match is stored.
 *
 * @param matches the season's matches of this one league; qualification
 *   matches (`is_qualification`) and cancelled ones are left out
 * @param rankByTeam place in the table by team name; pass an empty map when
 *   the table is not stored - stored ties are then ordered by kick-off alone
 */
export function buildKnockoutBracket<M extends BracketMatch>(
  matches: readonly M[],
  leagueCode: string,
  season: string,
  rankByTeam: ReadonlyMap<string, number>,
): BracketRound<M>[] {
  try {
    return buildRounds(matches, leagueCode, season, rankByTeam);
  } catch (bracketError) {
    if (!(bracketError instanceof RangeError)) throw bracketError;
    // The placeholder id cannot hold a league code that is not a number: no bracket for such a league.
    console.warn(`No play-off bracket for league code ${leagueCode}, season ${season}:`, bracketError);
    return [];
  }
}
